import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { type PaginatorState } from 'primeng/paginator';
import { MessageService } from 'primeng/api';

import { AuthService } from '@/core/auth/auth.service';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MapaUfCard } from '@/shared/components/mapa-uf-card/mapa-uf-card';
import { CardContent } from '@/shared/components/card-content/card-content';
import { EleicaoFeatureService } from './eleicao-feature.service';
import { EleicaoPleito } from '@/core/api/sicom-api/eleicoes/eleicao-pleito.model';
import { EleicaoParticipacaoUf } from '@/core/api/sicom-api/eleicoes/eleicao-participacao-uf.model';
import { EleicaoTipoParticipacao } from '@/core/api/sicom-api/eleicoes/eleicao-votante.model';
import { DetalhamentoPendencias } from '../detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorPendencia } from '../mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../mapa-pendencias/pendencia-linha.model';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';

type OpcaoTipo = { codigo: EleicaoTipoParticipacao; label: string };
type OpcaoPleito = { idPleito: number; label: string };

@Component({
    selector: 'app-participacao-eleitoral',
    standalone: true,
    imports: [CommonModule, FormsModule, CardModule, SkeletonModule, SelectModule, ButtonModule, PainelLateral, SelectEstado, MapaUfCard, CardContent, DetalhamentoPendencias],
    templateUrl: './participacao-eleitoral.html',
    styleUrl: './participacao-eleitoral.scss'
})
export class ParticipacaoEleitoral implements OnInit {
    readonly tiposParticipacao: OpcaoTipo[] = [
        { codigo: 'V', label: 'Votantes' },
        { codigo: 'N', label: 'Não votantes' },
        { codigo: 'J', label: 'Justificados' }
    ];
    readonly skeletons = [0, 1, 2, 3, 4, 5];

    pleitoOpcoes: OpcaoPleito[] = [];
    pleitoSelecionado: number | null = null;

    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;

    participacao: EleicaoParticipacaoUf[] = [];
    carregando = false;

    drillAtivo = false;
    drillUf: string | undefined = undefined;
    drillUfRotulo = 'Todas';
    drillTipo: EleicaoTipoParticipacao = 'V';
    votantesLinhas: PendenciaLinha[] = [];
    firstVotantes = 0;
    rowsVotantes = 100;
    totalVotantes = 0;
    carregandoVotantes = false;

    private ufTokenRestrita: string | null = null;
    private readonly participacaoChange$ = new Subject<void>();
    private readonly drillChange$ = new Subject<void>();

    constructor(
        private feature: EleicaoFeatureService,
        private authService: AuthService,
        private destroyRef: DestroyRef,
        private messageService: MessageService,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.carregarPleitos();
    }

    onUfSelecionadaChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUfSelecionada(uf);
        this.ufSelecaoGestao.definir(this.ufSelecionada);
    }

    aplicar(): void {
        this.voltarParaParticipacao();
        this.carregarParticipacao();
    }

    private obterDescricaoPleito(pleito: EleicaoPleito): string {
        const data = pleito.dtPleito ? new Date(pleito.dtPleito).toLocaleDateString('pt-BR') : '';
        return data ? `${pleito.dsPleito} (${data})` : pleito.dsPleito;
    }

    percentualParticipacao(linha: EleicaoParticipacaoUf): string {
        if (!linha.total) {
            return '0%';
        }
        return `${Math.round((linha.votantes / linha.total) * 100)}%`;
    }

    segmento(valor: number | undefined, total: number | undefined): number {
        return total ? ((valor ?? 0) / total) * 100 : 0;
    }

    get totais(): EleicaoParticipacaoUf {
        return this.participacao.reduce(
            (acc, l) => ({
                ufSgUf: 'BR',
                votantes: acc.votantes + (l.votantes ?? 0),
                naoVotantes: acc.naoVotantes + (l.naoVotantes ?? 0),
                justificados: acc.justificados + (l.justificados ?? 0),
                total: acc.total + (l.total ?? 0)
            }),
            { ufSgUf: 'BR', votantes: 0, naoVotantes: 0, justificados: 0, total: 0 } as EleicaoParticipacaoUf
        );
    }

    abrirDrill(uf: string | undefined, tipo: EleicaoTipoParticipacao): void {
        if (this.pleitoSelecionado === null) {
            return;
        }
        this.drillChange$.next();
        this.drillAtivo = true;
        this.drillUf = uf?.trim() || undefined;
        this.drillUfRotulo = this.drillUf ?? 'Todas as UFs';
        this.drillTipo = tipo;
        this.firstVotantes = 0;
        this.votantesLinhas = [];
        this.totalVotantes = 0;
        this.carregarVotantes();
    }

    onTipoDrillChange(tipo: EleicaoTipoParticipacao): void {
        this.drillChange$.next();
        this.drillTipo = tipo;
        this.firstVotantes = 0;
        this.votantesLinhas = [];
        this.totalVotantes = 0;
        this.carregarVotantes();
    }

    onPageChangeVotantes(event: PaginatorState): void {
        this.drillChange$.next();
        this.firstVotantes = event.first ?? 0;
        this.rowsVotantes = event.rows ?? this.rowsVotantes;
        this.carregarVotantes();
    }

    voltarParaParticipacao(): void {
        this.drillChange$.next();
        this.drillAtivo = false;
        this.drillUf = undefined;
        this.votantesLinhas = [];
        this.totalVotantes = 0;
        this.firstVotantes = 0;
        this.carregandoVotantes = false;
    }

    obterIndicadorDrill(): IndicadorPendencia | null {
        if (!this.drillAtivo) {
            return null;
        }
        const rotuloTipo = this.tiposParticipacao.find((t) => t.codigo === this.drillTipo)?.label ?? '';
        return {
            id: `eleicao-${this.drillTipo}`,
            titulo: rotuloTipo,
            descricao: `Participação eleitoral · ${this.drillUfRotulo}.`,
            grupo: 'Base',
            classeGrupo: 'grupo-registro',
            quantidade: this.totalVotantes || null
        };
    }

    private carregarPleitos(): void {
        this.feature
            .consultarPleitos()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (dados) => {
                    const pleitos = dados ?? [];
                    this.pleitoOpcoes = pleitos.map((pleito) => ({ idPleito: pleito.idPleito, label: this.obterDescricaoPleito(pleito) }));
                    this.pleitoSelecionado = this.pleitoOpcoes[0]?.idPleito ?? null;
                    // markForCheck: estado gravado em callback assíncrono sob o layout OnPush;
                    // sem marcar dirty a view só atualizaria após um clique.
                    this.cdr.markForCheck();
                    if (this.pleitoSelecionado !== null) {
                        this.carregarParticipacao();
                    }
                },
                error: () => {
                    this.pleitoOpcoes = [];
                    this.pleitoSelecionado = null;
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar os pleitos.' });
                    this.cdr.markForCheck();
                }
            });
    }

    private carregarParticipacao(): void {
        if (this.pleitoSelecionado === null) {
            this.participacao = [];
            return;
        }

        this.participacaoChange$.next();
        this.carregando = true;
        const uf = this.ufSelecionada ?? undefined;

        this.feature
            .consultarParticipacao(this.pleitoSelecionado, uf)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.participacaoChange$),
                finalize(() => {
                    this.carregando = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (dados) => (this.participacao = dados ?? []),
                error: () => {
                    this.participacao = [];
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar a participação eleitoral.' });
                }
            });
    }

    private carregarVotantes(): void {
        if (this.pleitoSelecionado === null) {
            return;
        }

        const size = this.rowsVotantes;
        const page = size > 0 ? Math.floor(this.firstVotantes / size) : 0;
        const tipo = this.drillTipo;
        const uf = this.drillUf;

        this.carregandoVotantes = true;
        this.feature
            .consultarVotantes(this.pleitoSelecionado, uf, tipo, page, size)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.drillChange$),
                finalize(() => {
                    if (this.drillTipo === tipo && this.drillUf === uf) {
                        this.carregandoVotantes = false;
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (resp) => {
                    if (this.drillTipo !== tipo || this.drillUf !== uf) {
                        return;
                    }
                    this.votantesLinhas = (resp?.content ?? []).map((item) => ({
                        uf: item.ufSgUf ?? '',
                        crm: item.nuCrm ?? '',
                        nome: item.nmSolicitante ?? ''
                    }));
                    this.totalVotantes = resp?.totalElements ?? 0;
                },
                error: () => {
                    if (this.drillTipo !== tipo || this.drillUf !== uf) {
                        return;
                    }
                    this.votantesLinhas = [];
                    this.totalVotantes = 0;
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar o detalhamento de participantes.' });
                }
            });
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufTokenValida = /^[A-Z]{2}$/.test(ufToken) ? ufToken : '';

        if (ufTokenValida && ufTokenValida !== 'BR') {
            this.ufTokenRestrita = ufTokenValida;
            this.ufsPermitidas = [ufTokenValida];
            this.ufSelecionada = ufTokenValida;
            this.ufSelecaoGestao.definir(ufTokenValida);
            return;
        }

        this.ufTokenRestrita = null;
        this.ufsPermitidas = null;
        this.ufSelecionada = this.ufSelecaoGestao.uf();
    }

    private normalizarUfSelecionada(uf?: string | null): string | null {
        const ufNormalizada = String(uf ?? '')
            .trim()
            .toUpperCase();

        if (!ufNormalizada || ufNormalizada === 'BR' || !/^[A-Z]{2}$/.test(ufNormalizada)) {
            return this.ufTokenRestrita;
        }

        if (this.ufTokenRestrita && ufNormalizada !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }

        return ufNormalizada;
    }
}
